import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FirebaseContext } from 'common';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { formatMoney } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

export default function Subscriptions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerSlug } = useParams();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, partnerKey, paths, websiteContent } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const gateInactive = Boolean(partner?.inactive);
  const gateComingSoon = Boolean(partner && isPartnerComingSoon(partner) && !isAdmin);
  const gateMaintenance = Boolean(partner && isPartnerMaintenance(partner) && !isAdmin);

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});

  const shop = websiteContent.shop || {};
  const shopEnabled = shop.enabled !== false;
  const currency = shop.currency || 'USD';
  const plans = useMemo(() => {
    const list = Array.isArray(shop.subscriptions) ? shop.subscriptions.filter((p) => p && p.published !== false) : [];
    return list.map((p) => getLocalizedShopItem(p, i18n.language));
  }, [shop.subscriptions, i18n.language]);

  const uid = auth?.profile?.uid;
  const [userPlans, setUserPlans] = useState({});
  const [busyPlanId, setBusyPlanId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (gateInactive || gateComingSoon || gateMaintenance) {
      setUserPlans({});
      return;
    }
    if (!firebase?.database || !uid) {
      setUserPlans({});
      return;
    }
    const ref = firebase.database.ref(
      partnerKey ? `shop_user_subscriptions/${uid}/${partnerKey}` : `shop_user_subscriptions/${uid}`
    );
    ref.on('value', (snap) => setUserPlans(snap.val() || {}));
    return () => ref.off();
  }, [firebase?.database, uid, partnerKey, gateInactive, gateComingSoon, gateMaintenance]);

  const requireLogin = (from) => {
    navigate('/login', { state: { from } });
  };

  const subscribe = async (plan) => {
    setError('');
    setSuccess('');
    if (!uid) return requireLogin(paths.subscriptions);
    if (!firebase?.database?.ref) {
      setError(t('checkout_unavailable') || 'This feature is unavailable right now.');
      return;
    }
    if (!plan?.id) return;

    setBusyPlanId(plan.id);
    try {
      const record = {
        subscriptionId: plan.id,
        title: plan.title || '',
        price: Number(plan.price || 0),
        interval: plan.interval || 'month',
        status: 'active',
        startedAt: new Date().toISOString(),
        userId: uid,
        partnerSlug: partnerKey || null,
      };
      const basePath = partnerKey ? `shop_user_subscriptions/${uid}/${partnerKey}` : `shop_user_subscriptions/${uid}`;
      await firebase.database.ref(`${basePath}/${plan.id}`).set(record);
      setSuccess(t('subscription_activated') || 'Subscription activated.');
    } catch (e) {
      console.error('Failed to subscribe:', e);
      setError(t('subscription_failed') || 'Failed to activate subscription. Please try again.');
    } finally {
      setBusyPlanId('');
    }
  };

  const cancel = async (planId) => {
    setError('');
    setSuccess('');
    if (!uid) return requireLogin(paths.subscriptions);
    if (!firebase?.database?.ref || !planId) return;
    setBusyPlanId(planId);
    try {
      const basePath = partnerKey ? `shop_user_subscriptions/${uid}/${partnerKey}` : `shop_user_subscriptions/${uid}`;
      await firebase.database.ref(`${basePath}/${planId}`).update({
        status: 'canceled',
        canceledAt: new Date().toISOString(),
      });
      setSuccess(t('subscription_canceled') || 'Subscription canceled.');
    } catch (e) {
      console.error('Failed to cancel subscription:', e);
      setError(t('subscription_cancel_failed') || 'Failed to cancel subscription. Please try again.');
    } finally {
      setBusyPlanId('');
    }
  };

  if (gateInactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (gateComingSoon) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (gateMaintenance) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  return (
    <PublicLayout title={t('subscriptions') || 'Subscriptions'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: { xs: 32, md: 44 }, letterSpacing: -1 }}>
              {t('subscriptions') || 'Subscriptions'}
            </Typography>
            <Typography sx={{ color: theme.muted, mt: 1, maxWidth: 680 }}>
              {t('subscriptions_subtitle') || 'Choose a plan and unlock member-only perks.'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate(paths.shop)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            {t('back_to_shop') || 'Back to shop'}
          </Button>
        </Stack>

        <Box sx={{ mt: 3 }}>
          {!shopEnabled ? (
            <Alert severity="info">{t('shop_disabled_help') || 'Please check back later.'}</Alert>
          ) : null}
          {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}
          {success ? <Alert severity="success" sx={{ mt: 1 }}>{success}</Alert> : null}
        </Box>

        {!plans.length ? (
          <Box sx={{ mt: 4, p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}>
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('no_subscriptions') || 'No subscriptions yet'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('no_subscriptions_help') || 'Ask an admin to add plans in Storefront > Shop > Subscriptions.'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5} sx={{ mt: 2 }}>
            {plans.map((plan) => {
              const record = plan?.id ? userPlans?.[plan.id] : null;
              const active = record?.status === 'active';
              const canceled = record?.status === 'canceled';
              const priceLabel = formatMoney(plan.price, currency) + ` / ${plan.interval || 'month'}`;

              return (
                <Grid key={plan.id || plan.title} item xs={12} md={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      borderColor: theme.border,
                      bgcolor: theme.cardBg,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                          <Stack spacing={0.5}>
                            <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18 }}>
                              {plan.title || 'Plan'}
                            </Typography>
                            <Typography sx={{ color: theme.muted }}>{priceLabel}</Typography>
                          </Stack>
                          {active ? (
                            <Chip label={t('active') || 'Active'} color="success" size="small" sx={{ fontWeight: 900 }} />
                          ) : canceled ? (
                            <Chip label={t('canceled') || 'Canceled'} size="small" sx={{ fontWeight: 900 }} />
                          ) : (
                            <Chip
                              icon={<WorkspacePremiumRoundedIcon />}
                              label={t('popular') || 'Member'}
                              size="small"
                              sx={{ fontWeight: 900, bgcolor: theme.sectionAltBg }}
                            />
                          )}
                        </Stack>

                        {plan.description ? (
                          <Typography variant="body2" sx={{ color: theme.muted }}>
                            {plan.description}
                          </Typography>
                        ) : null}

                        {Array.isArray(plan.perks) && plan.perks.length ? (
                          <>
                            <Divider />
                            <Stack spacing={0.75}>
                              {plan.perks.slice(0, 6).map((perk) => (
                                <Typography key={perk} variant="body2" sx={{ color: theme.text }}>
                                  {'• ' + perk}
                                </Typography>
                              ))}
                            </Stack>
                          </>
                        ) : null}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                          {!uid ? (
                            <Button
                              variant="contained"
                              onClick={() => requireLogin(paths.subscriptions)}
                              sx={{
                                flex: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 900,
                                bgcolor: theme.accent || '#111827',
                                '&:hover': { bgcolor: theme.accent || '#0b1224' },
                              }}
                            >
                              {t('login') || 'Log in'}
                            </Button>
                          ) : active ? (
                            <Button
                              variant="outlined"
                              onClick={() => cancel(plan.id)}
                              disabled={busyPlanId === plan.id}
                              sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                            >
                              {busyPlanId === plan.id ? (t('saving') || 'Saving...') : (t('cancel') || 'Cancel')}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              onClick={() => subscribe(plan)}
                              disabled={busyPlanId === plan.id}
                              sx={{
                                flex: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 900,
                                bgcolor: theme.accent || '#111827',
                                '&:hover': { bgcolor: theme.accent || '#0b1224' },
                              }}
                            >
                              {busyPlanId === plan.id ? (t('saving') || 'Saving...') : (t('subscribe') || 'Subscribe')}
                            </Button>
                          )}

                          <Button
                            variant="text"
                            onClick={() => navigate('/shop')}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, color: theme.link }}
                          >
                            {t('browse_shop') || 'Browse shop'}
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </PublicLayout>
  );
}
